import React, { useCallback, useState, useEffect } from 'react';
import { Table, Space, Tooltip, Row, Col, Spin } from 'antd';
import { 
  TableWrapper, 
  SeminarEditModal, 
  SeminarScheduleEditModal } from 'components/molecules';
import { usePopupManager } from 'react-popup-manager';
import { ColumnsType } from 'antd/lib/table/interface';
import { TotalNumMenu } from 'components/atoms';
import { useLocation, useNavigate } from 'react-router';
import { addNewSearch } from 'utils/utils';
import { PlusSquareOutlined, MinusSquareOutlined, CarryOutOutlined } from '@ant-design/icons';
import { useTableInfo } from 'hooks';
import { INIT_PAGESIZE } from 'commons/constants';
import qs from 'query-string';
import * as XLSX from 'xlsx';
import { fetcher } from 'services';

interface FormItemProps {
  title: string | undefined;
  projectList: B2cListDto[] | undefined;  
  searchResult: any;
  hiddenColumns: string[];
  register: boolean;
  useExcelDownload?: boolean;
  refetch: () => void;
}

const SEMINARTable = ({title, projectList, searchResult, hiddenColumns, register, useExcelDownload = false, refetch}: FormItemProps): React.ReactElement => {
  /* ======================================================
   * variables
   * ====================================================== */
  const navigate = useNavigate();
  const location = useLocation();
  const { open } = usePopupManager();
  const [rowExpaned, setRowExpaned] = useState<number[]>([]);  
  const searchParams = qs.parse(location.search);
  const [isLoding, setIsLoading] = useState<boolean>(false);  

   const onCellClick = (record) => {
    return {
      onClick: (e) => {
        open(SeminarEditModal, {id: Number(record.id), onClose: (isOk)=>{if(isOk) {refetch();}}});            
      },
    };
   };

  const columns: ColumnsType<B2cListDto> = [
    {
      title: '번호',
      dataIndex: 'id',
      key: 'id',
      width: '70px',
      align: 'center',
      onCell: (record) => {
        return {
            onClick: (e) => {
              if (rowExpaned.includes(record.id)) {
                setRowExpaned(rowExpaned.filter(item => item !== record.id) ) 
              } else {
                setRowExpaned(preRowExpaned => [...preRowExpaned, record.id])
              }              
            },
        };
      },       
    },  
    {
      title: '구분',
      dataIndex: 'close_yn',
      key: 'close_yn',
      width: '70px',
      align: 'center',
      onCell: onCellClick,
      sorter: (a, b) => a.close_yn.localeCompare(b.close_yn),
      render: (close_yn, record) => {
        return (
          record.status_type==='drop'?'폐강':close_yn==='Y'?'마감':'모집중'
        )
      }       
    },     
    {
      title: '세미나명',
      dataIndex: 'lecture_title',
      key: 'lecture_title',
      width: 'auto',
      align: 'left', 
      ellipsis: true,  
      onCell: onCellClick, 
      sorter: (a, b) => a.lecture_title.localeCompare(b.lecture_title),
    },  
    {
      title: '주제',
      dataIndex: 'group_subject',
      key: 'group_subject',
      width: '150px',
      align: 'center',
      ellipsis: true, 
      onCell: onCellClick,
    },  
    {
      title: '강사',
      dataIndex: 'group_lecturer',
      key: 'group_lecturer',
      width: '150px',
      align: 'center',
      ellipsis: true, 
      onCell: onCellClick,
    }, 
    {
      title: '메모',
      dataIndex: 'memo',
      key: 'memo',
      width: '50px',
      align: 'center',
      ellipsis: true,
      onCell: onCellClick,
      render: (memo, record) => {
        return (
          (memo)&&(
            <Tooltip placement="topLeft" title={memo}>
              <CarryOutOutlined />
            </Tooltip>
          )
        )
      } 
    },
    {
      title: '일정',
      dataIndex: 'start_date',
      key: 'start_date',
      width: '180px',
      align: 'center',
      ellipsis: true,
      onCell: onCellClick,
      sorter: (a, b) => a.start_date.localeCompare(b.start_date),
      render: (start_date, record) => {
       return (
        <label>{start_date} ~ {record.end_date}</label>
       )
     }   
    },  
    {
      title: '시간',
      dataIndex: 'group_time',
      key: 'group_time',
      width: '100px',
      align: 'center',
      ellipsis: true, 
      onCell: onCellClick,
    }, 
    {
      title: '장소구분',
      dataIndex: 'group_lecture_place_type',
      key: 'group_lecture_place_type',
      width: '100px',
      align: 'center',
      ellipsis: true, 
      onCell: onCellClick,
    },  
    {
      title: '장소',
      dataIndex: 'group_location',
      key: 'group_location',
      width: 'auto',
      align: 'center',
      ellipsis: true, 
      onCell: onCellClick,
    },  
 
  {
   title: '스케줄',
   key: 'schedule',
   width: '80px',
   align: 'center',
   render: (record) => {
    return (
      <>
        <a onClick={(e) => {
          if (rowExpaned.includes(record.id)) {
            setRowExpaned(rowExpaned.filter(item => item !== record.id) ) 
          } else {
            setRowExpaned(preRowExpaned => [...preRowExpaned, record.id])
          }
        }}>{record.schedule.length}건</a>
        {(register) && (
          <>
            &nbsp;|&nbsp;
            <a onClick={(e) => {
              onScheduleEditPopup(record.id, 0, record.lecture_gid, record.lecture_cha);
    
            }}>등록</a>
          </>
        )}
      </>
    )
  }
 },  
    {
     title: '신청인원',
     dataIndex: 'student_cnt',
     key: 'student_cnt',
     width: '80px',
     align: 'center',
     ellipsis: true,
     sorter: (a, b) => a.student_cnt - b.student_cnt, 
     onCell: (record) => {
      return {
          onClick: (e) => {
            window.location.href = "/b2c/seminar/student/" + record.id;
          },
      };
    },     
   },
  ];

  /* ======================================================
  * events
  * ====================================================== */ 

 const onClickRegister = () => {
  open(SeminarEditModal, {
    id: 0, 
    onClose: (isOk)=>{
      if(isOk) {
        refetch();
      }
    }}); 
 }

 const onChangePageInfo = (pageNo: number, pageSize: number) => {
  navigate({
    ...location,
    search: addNewSearch(location.search, { pageNo, pageSize }),
  });
};  

const onScheduleEditPopup = (group_id: number, id: number, lecture_gid: number, lecture_cha: number) => {
 open(SeminarScheduleEditModal, {
   group_id: group_id,
   id: id,
   onClose: (isOk) => {
     if (isOk) {
       refetch();
     }
   },
 }); 
}

const onExcelDownload = () => {
  setIsLoading(true); 
  fetcher.b2c.postProjectSeminarExcelList({
    pageNo: 1,
    pageSize: 0,
    searchValue: searchParams.search ? String(searchParams.search) : '',  
    searchDateStart: searchParams.searchDateStart ? String(searchParams.searchDateStart) : '',   
    searchDateEnd: searchParams.searchDateEnd ? String(searchParams.searchDateEnd) : '',  
    pm: searchParams.pm ? String(searchParams.pm) : '', 
  })
  .then((res) => {
      switch (res.code) {
          case 200:

          if (res.result) {
            const book = XLSX.utils.book_new();
            const nurses = XLSX.utils.json_to_sheet(
              (res.result.list as SeminarExcelListDto[]).map((item, index) => {
                return {
                  '번호' : item.project_id,
                  '구분' : item.project_close_yn,
                  '세미나명': item.project_title,
                  '일정': item.project_date,
                  '시간': item.project_time,
                  '장소구분': item.project_lecture_place_type,
                  '장소': item.project_location,
                  '신청인원': item.project_student_cnt,
                  '주제1': item.lecturer_subject1,
                  '시간1': item.lecturer_time1,
                  '강사1': item.lecturer_name1,
                  '주제2': item.lecturer_subject2,
                  '시간2': item.lecturer_time2,
                  '강사2': item.lecturer_name2,
                  '주제3': item.lecturer_subject3,
                  '시간3': item.lecturer_time3,
                  '강사3': item.lecturer_name3,  
                  '주제4': item.lecturer_subject4,
                  '시간4': item.lecturer_time4,
                  '강사4': item.lecturer_name4,  
                  '주제5': item.lecturer_subject5,
                  '시간5': item.lecturer_time5,
                  '강사5': item.lecturer_name5,  
                  '주제6': item.lecturer_subject6,
                  '시간6': item.lecturer_time6,
                  '강사6': item.lecturer_name6,  
                  '주제7': item.lecturer_subject7,
                  '시간7': item.lecturer_time7,
                  '강사7': item.lecturer_name7,  
                  '주제8': item.lecturer_subject8,
                  '시간8': item.lecturer_time8,
                  '강사8': item.lecturer_name8,  
                  '주제9': item.lecturer_subject9,
                  '시간9': item.lecturer_time9,
                  '강사9': item.lecturer_name9,  
                  '주제10': item.lecturer_subject10,
                  '시간10': item.lecturer_time10,
                  '강사10': item.lecturer_name10,                  
                }
              }), 
              { 
                header : [
                  '번호',
                  '구분',
                  '세미나명',
                  '일정',
                  '시간',
                  '장소구분',
                  '장소',
                  '신청인원',
                  '주제1',
                  '시간1',
                  '강사1',
                  '주제2',
                  '시간2',
                  '강사2',
                  '주제3',
                  '시간3',
                  '강사3',
                  '주제4',
                  '시간4',
                  '강사4',
                  '주제5',
                  '시간5',
                  '강사5',
                  '주제6',
                  '시간6',
                  '강사6',
                  '주제7',
                  '시간7',
                  '강사7',
                  '주제8',
                  '시간8',
                  '강사8',
                  '주제9',
                  '시간9',
                  '강사9',
                  '주제10',
                  '시간10',
                  '강사10',
                ], 
                skipHeader : false 
              } 
            );
        
            nurses["!cols"] = [
              { wpx : 60 } 
            , { wpx : 80 } 
            , { wpx : 240 }  
            , { wpx : 100 }  
            , { wpx : 120 } 
            , { wpx : 100 } 
            , { wpx : 150 } 
            , { wpx : 60 } 
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 80 } 
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 80 } 
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 80 } 
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 80 }  
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 80 }  
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 80 }  
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 80 }  
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 80 }  
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 80 }  
            , { wpx : 120 } 
            , { wpx : 120 } 
            , { wpx : 80 }  
            ]
        
            XLSX.utils.book_append_sheet( book, nurses, "NURSES" );
        
            XLSX.writeFile( book, "세미나.xlsx" ); 
          }

          break;
          default:  
          break;
      };
      setIsLoading(false);         
  });
}  

  /* ======================================================
  * hooks
  * ====================================================== */
  const { pagination, onTableChange } = useTableInfo({
    pageInfo: {
      pageNo: Number(searchParams.pageNo) || 1,
      pageSize: Number(searchParams.pageSize) || INIT_PAGESIZE,
    },
    datas: projectList,
    totalCount: searchResult.totalCount,
    onChangePageInfo,
  });

  useEffect(() => {
    setRowExpaned([]);
  }, [searchResult.pageNo]); 

  const onExpaned = (val: boolean) => {
    if (val) {
      if (!!projectList) {
        const rows: any[] = [];
        projectList.forEach(record => {
          rows.push(record.id);
        })
  
        setRowExpaned(rows);
      }
    } else {
      setRowExpaned([]);
    }
  };

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Spin spinning={isLoding} tip="처리중입니다. 잠시만 기다려 주세요." size="large">
        <TotalNumMenu onExpaned={onExpaned} text={title} totalNum={searchResult.totalCount} useRegister={register} useExcelDownload={useExcelDownload} onUseRegister={onClickRegister} onDownloadExcel={onExcelDownload}/>
        <TableWrapper isSearch={!!searchParams.search || !!searchParams.lecturer_type || !!searchParams.lecturer_category}>
          <Table
            tableLayout="fixed"
            dataSource={projectList}
            columns={columns.filter((item) => hiddenColumns.findIndex((col)=>col === item.key) < 0)}
            pagination={(!!searchResult&&searchResult.pageSize>0)?pagination:false}
            onChange={onTableChange}
            style={{ width: '100%', overflowX: 'auto', minWidth: 600 }}
            className="columnCenterFixTable clickable"
            showSorterTooltip={false}
            rowKey={'id'}
            expandable={{
              expandedRowRender: (record) => (
                <Space direction="vertical" style={{padding:"5px 30px", width:"100%"}} size={3}>
                {record.schedule.map((schedule)=>( 
                  <Row key={schedule.id} style={{padding:"0px 15px", width:"100%"}}>
                    <Col span={3}><a onClick={()=> onScheduleEditPopup(record.id, schedule.id, record.lecture_gid, record.lecture_cha)}>{schedule.time}</a></Col>
                    <Col span={1}>{schedule.lecturer}</Col>
                    <Col span={3}>{schedule.lecture_subject}</Col>
                    <Col span={2}>{schedule.lecture_place_type}</Col>
                    <Col span={2}>{schedule.pm}</Col>
                    <Col span={2}>{schedule.cm}</Col>
                    <Col span={10} style={{textOverflow:"ellipsis",whiteSpace:"nowrap",overflow:"hidden"}}><Tooltip placement="topLeft" title={schedule.location}>{schedule.location}</Tooltip></Col>
                    <Col span={1} style={{textAlign:"right"}}>{schedule.class_evaluation && schedule.class_evaluation > 0 && schedule.class_evaluation}</Col>                    
                  </Row>
                ))}
                </Space>
              ),
              columnWidth: 25,
              expandedRowKeys: rowExpaned,
              onExpand: (expaned, record) => { 
                if (expaned) {
                  setRowExpaned(preRowExpaned => [...preRowExpaned, record.id])
                } else {
                  setRowExpaned(rowExpaned.filter(item => item !== record.id) ) 
                }
              },                
            }}
          />
        </TableWrapper> 
      </Spin>
    </Space>
  );
};

export default SEMINARTable;
